import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, NavController, ViewDidEnter } from '@ionic/angular';
import {
    LoginFormComponent,
    LoginFormOutput,
} from '@lib/frontend-shared-authentication-ui-authentication-forms';
import { ActivatedRoute, Params, RouterLink } from '@angular/router';
import { AuthenticationService } from '@lib/frontend-shared-accounts-data-authentication-service';
import { InsertRouteTitleDirective } from '@lib/frontend-shared-util-route-title';
import { Uuid } from '@lib/shared-interface-utility-types';
import { map, Observable, shareReplay } from 'rxjs';
import { AccountsService } from '@lib/frontend-shared-accounts-data-accounts-service';
import { hasErrorProperties } from '@lib/shared-util-errors';
import { ToastService } from '@lib/frontend-shared-util-toast-service';
import { Account } from '@lib/shared-interface-account';
import { RefreshWindowDirective } from '@lib/frontend-shared-util-ionic-helpers';

@Component({
    standalone: true,
    selector: 'lib-internal-login',
    templateUrl: './login.page.html',
    styleUrls: ['./login.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        InsertRouteTitleDirective,
        IonicModule,
        LoginFormComponent,
        RefreshWindowDirective,
        RouterLink,
    ],
})
export class LoginPage implements ViewDidEnter, OnInit {
    protected queryParams$!: Observable<Params | undefined>;

    @ViewChild(LoginFormComponent, { static: true })
    private readonly loginForm!: LoginFormComponent;

    public constructor(
        private readonly accountsService: AccountsService,
        private readonly authenticationService: AuthenticationService,
        private readonly navigationController: NavController,
        private readonly route: ActivatedRoute,
        private readonly toastService: ToastService,
    ) {}

    public ionViewDidEnter() {
        const email = this.route.snapshot.queryParamMap.get('email') ?? undefined;
        this.loginForm.fillAndFocus(email);
    }

    public ngOnInit() {
        // This is available because of `static: true`.
        this.queryParams$ = this.loginForm.username$.pipe(
            map((email) => (email ? { email } : undefined)),
            shareReplay(1),
        );
    }

    public async submitLogin(data: LoginFormOutput): Promise<void> {
        const accountId = await this.login(data);
        if (!accountId) return;

        // Todo: this redirect param is wiped out if the user decides to reset their password.
        //  If we need to pass along the redirect throughout additional login routes,
        //  we may need to come up with a different approach.
        const redirectUrl = this.route.snapshot.queryParamMap.get('redirect');
        await this.navigationController.navigateRoot(redirectUrl ?? '/');
    }

    public async login(credentials: LoginFormOutput): Promise<Uuid | undefined> {
        try {
            await this.authenticationService.signIn(credentials);
        } catch (error) {
            if (isUserNotConfirmedException(error)) {
                return void this.navigationController.navigateRoot([
                    'account',
                    'confirm-registration',
                    credentials.username,
                ]);
            }

            const message = getSignInErrorMessage(error);
            await this.toastService.presentError({ message });
            return;
        }

        let account: Account | undefined;
        try {
            account = await this.accountsService.refreshUser();
        } catch (error) {
            const message = getRefreshedAccountError(error);
            await this.toastService.presentError({ message });
            // Since we logged into cognito successfully but were unable to pull the account data,
            // we log out so a future refresh won't load in the wrong authentication id again.
            await this.authenticationService.signOut(false);
            return;
        }

        if (!account) return;
        if (!account.active) {
            const message = loginErrorMessages.accessRevoked;
            await this.toastService.presentError({ message });
            return;
        }

        return account.id;
    }
}

function isUserNotConfirmedException(error: unknown): boolean {
    return hasErrorProperties(error) && error.name === 'UserNotConfirmedException';
}

function getSignInErrorMessage(error: unknown): string {
    if (!hasErrorProperties(error)) return loginErrorMessages.unknown;

    // One might be tempted to check using instanceof,
    // but this library doesn't export its exceptions!
    const invalidEmailOrPassword =
        error.name === 'UserNotFoundException' ||
        error.name === 'InvalidPasswordException' ||
        error.name === 'NotAuthorizedException';

    if (invalidEmailOrPassword) return loginErrorMessages.doesNotMatchRecords;
    if (error.name === 'LimitExceededException') return loginErrorMessages.tooManyRequests;

    return loginErrorMessages.unknown;
}

function getRefreshedAccountError(error: unknown): string {
    if (!hasErrorProperties(error)) return loginErrorMessages.unknown;
    if (error.message.startsWith('Http failure response'))
        return loginErrorMessages.serviceUnavailable;
    if (error.message === 'Forbidden resource') return loginErrorMessages.accessForbidden;
    if (error.message === 'Account can not be registered without an invitation.') {
        return loginErrorMessages.missingInvitation;
    }

    return loginErrorMessages.unknownContactSupport;
}

// Todo: When i18n is implemented, this object can go away
//  and the getter functions can just directly return the string of the translation file path.
const loginErrorMessages = {
    accessForbidden: 'Your account does not have access to this application.',
    accessRevoked: 'Your account access has been revoked.',
    doesNotMatchRecords: 'Email and password do not match our records.',
    missingInvitation: 'An invitation is required to log in.',
    multipleErrors: 'One or more errors occurred while retrieving the account.',
    serviceUnavailable: 'Login service temporarily unavailable. Please try again later.',
    tooManyRequests: 'You have made too many requests. Please try again later.',
    unknown: 'An unknown error occurred during login.',
    unknownContactSupport:
        'An unknown error occurred during login. Please contact customer support.',
} as const;
